import React from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { MessageCircle } from 'lucide-react';
import { contactDisplayName, contactNumberLine } from '../../utils/contact-display';

const AVATAR_COLORS = [
  ['#8B5CF6', '#6366F1'], ['#3B82F6', '#06B6D4'], ['#EC4899', '#F472B6'],
  ['#F97316', '#FB923C'], ['#14B8A6', '#10B981'], ['#EF4444', '#F87171'],
  ['#EAB308', '#FACC15'], ['#6366F1', '#8B5CF6'], ['#10B981', '#34D399'],
  ['#F59E0B', '#FBBF24'],
];

function getAvatarColors(name = '') {
  let hash = 0;
  for (let i = 0; i < name.length; i++) {
    hash = name.charCodeAt(i) + ((hash << 5) - hash);
  }
  return AVATAR_COLORS[Math.abs(hash) % AVATAR_COLORS.length];
}

function formatTime(date) {
  if (!date) return '';
  const d = new Date(date);
  if (isNaN(d.getTime())) return '';
  const now = new Date();
  const isToday = d.toDateString() === now.toDateString();
  if (isToday) return d.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' });
  const diffDays = Math.floor((now - d) / (1000 * 60 * 60 * 24));
  if (diffDays < 7) {
    const days = ['Dim', 'Lun', 'Mar', 'Mer', 'Jeu', 'Ven', 'Sam'];
    return days[d.getDay()];
  }
  return d.toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit' });
}

export default function ConversationList({
  conversations,
  selectedId,
  onSelect,
  unreadCounts = {},
}) {
  if (conversations.length === 0) {
    return (
      <div className="flex-1 flex flex-col items-center justify-center p-8 text-center">
        <div className="w-16 h-16 rounded-2xl bg-primary-dark/60 flex items-center justify-center mb-3">
          <MessageCircle className="w-8 h-8 text-gray-600" />
        </div>
        <p className="text-text-gray text-sm">Aucune conversation</p>
      </div>
    );
  }

  // Always compare as strings to handle ObjectId vs string mismatch
  const selectedIdStr = selectedId != null ? String(selectedId) : null;

  return (
    <div className="flex-1 overflow-y-auto px-2 py-2 space-y-1">
      <AnimatePresence initial={false}>
        {conversations.map((conv) => {
          const convIdStr = conv._id != null ? String(conv._id) : '';
          const name = contactDisplayName(conv);
          const numberLine = contactNumberLine(conv);
          const initials = name.substring(0, 2).toUpperCase() || '??';
          const [c1, c2] = getAvatarColors(name);
          // Use string key for unread count lookup
          const unread = unreadCounts[convIdStr] || 0;
          const isSelected = selectedIdStr === convIdStr;
          const lastMsg =
            typeof conv.lastMessage?.content === 'string'
              ? conv.lastMessage.content
              : '';
          const preview = lastMsg.length > 50 ? lastMsg.slice(0, 50) + '…' : lastMsg;

          return (
            <motion.div
              key={convIdStr || conv.clientId}
              initial={{ opacity: 0, x: -16 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -16 }}
              transition={{ duration: 0.18 }}
              whileHover={{ x: 2 }}
              onClick={() => onSelect(conv)}
              className={`relative p-2.5 rounded-2xl cursor-pointer transition-all ${
                isSelected
                  ? 'bg-gradient-to-r from-accent-green/15 to-transparent ring-1 ring-accent-green/30'
                  : 'hover:bg-primary-dark/50'
              }`}
            >
              {isSelected && (
                <span className="absolute left-0 top-1/2 -translate-y-1/2 w-1 h-8 rounded-r-full bg-gradient-to-b from-accent-green to-emerald-500" />
              )}
              <div className="flex items-center gap-3">
                {/* Avatar */}
                <div className="relative flex-shrink-0">
                  <div
                    className="w-11 h-11 rounded-2xl flex items-center justify-center font-bold text-white text-sm select-none shadow-md"
                    style={{ background: `linear-gradient(135deg, ${c1}, ${c2})` }}
                  >
                    {initials}
                  </div>
                  {conv.status === 'active' && (
                    <div className="absolute -bottom-0.5 -right-0.5 w-3.5 h-3.5 bg-green-500 rounded-full border-2 border-primary-card shadow-sm" />
                  )}
                </div>

                {/* Content */}
                <div className="flex-1 min-w-0">
                  <div className="flex items-center justify-between mb-0.5">
                    <span
                      className={`text-sm font-semibold truncate pr-2 ${
                        unread > 0 ? 'text-white' : 'text-text-light'
                      }`}
                    >
                      {name}
                    </span>
                    <span className={`text-[11px] flex-shrink-0 ${unread > 0 ? 'text-accent-green font-semibold' : 'text-text-gray'}`}>
                      {formatTime(conv.lastMessageAt)}
                    </span>
                  </div>
                  <p className={`text-[10px] truncate mb-0.5 ${numberLine ? 'text-text-gray' : 'text-text-gray/50 italic'}`}>
                    {numberLine || 'Numéro non disponible'}
                  </p>
                  <div className="flex items-center justify-between gap-2">
                    <p className={`text-xs truncate ${unread > 0 ? 'text-text-light' : 'text-text-gray'}`}>
                      {preview}
                    </p>
                    {unread > 0 && (
                      <span className="flex-shrink-0 bg-gradient-to-br from-accent-green to-emerald-500 text-black text-[10px] font-bold rounded-full min-w-[18px] h-[18px] flex items-center justify-center px-1 shadow-sm shadow-accent-green/30">
                        {unread > 9 ? '9+' : unread}
                      </span>
                    )}
                  </div>
                </div>
              </div>
            </motion.div>
          );
        })}
      </AnimatePresence>
    </div>
  );
}
